import React, { useState, useEffect } from 'react';
import { db } from '../firebase-config';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { X, Megaphone } from 'lucide-react';

const AnnouncementDisplay = () => {
    const [announcement, setAnnouncement] = useState(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const fetchAnnouncement = async () => {
            try {
                const announcementsRef = collection(db, 'artifacts/whispers-of-harmony/public/data/announcements');
                const q = query(announcementsRef, where('status', '==', 'published'), orderBy('createdAt', 'desc'), limit(1));
                const querySnapshot = await getDocs(q);

                if (!querySnapshot.empty) {
                    const latestAnnouncement = { ...querySnapshot.docs[0].data(), id: querySnapshot.docs[0].id };
                    const closedAnnouncementId = localStorage.getItem('closedAnnouncementId');
                    
                    if (closedAnnouncementId !== latestAnnouncement.id) {
                        setAnnouncement(latestAnnouncement);
                        setIsVisible(true);
                    }
                }
            } catch (error) {
                console.error("Error fetching announcement:", error);
            }
        };

        fetchAnnouncement();
    }, []);

    const handleClose = () => {
        setIsVisible(false);
        if (announcement) {
            localStorage.setItem('closedAnnouncementId', announcement.id);
        }
    };

    // A simple parser to handle basic markdown-like formatting
    const renderContent = (text) => {
        if (!text) return null;
        return text.split('\n').map((line, index) => {
            line = line.trim();
            if (line.startsWith('* ') || line.startsWith('- ')) {
                return (
                    <li key={index} className="flex items-start">
                        <span className="mr-2 mt-1 text-indigo-300">&#8226;</span>
                        <span>{line.substring(2)}</span>
                    </li>
                );
            }
            return <p key={index} className="mb-2 last:mb-0">{line}</p>;
        });
    };

    if (!isVisible || !announcement) {
        return null;
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div 
                className="w-full max-w-2xl bg-slate-800 bg-opacity-50 backdrop-blur-xl border border-slate-600 rounded-2xl shadow-2xl text-white overflow-hidden"
                style={{
                    background: 'radial-gradient(circle at top left, rgba(129, 140, 248, 0.15), transparent), radial-gradient(circle at bottom right, rgba(56, 189, 248, 0.1), transparent), rgba(15, 23, 42, 0.7)',
                    boxShadow: '0 0 80px rgba(0,0,0,0.5)'
                }}
            >
                <div className="p-8">
                    <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center">
                            <div className="w-12 h-12 bg-indigo-500/20 rounded-full flex items-center justify-center mr-4 border border-indigo-500/50">
                                <Megaphone size={24} className="text-indigo-300" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-indigo-300">{announcement.title}</h2>
                                <p className="text-sm text-slate-400">Latest Update</p>
                            </div>
                        </div>
                        <button onClick={handleClose} className="text-slate-400 hover:text-white transition-colors duration-200">
                            <X size={28} />
                        </button>
                    </div>

                    <div className="prose prose-invert max-w-none text-slate-300 leading-relaxed text-base pr-4 max-h-[60vh] overflow-y-auto custom-scrollbar">
                        {announcement.content && <div>{renderContent(announcement.content)}</div>}
                        
                        {announcement.patchNotes && (
                            <div className="mt-6 pt-6 border-t border-slate-700">
                                <h3 className="text-xl font-semibold mb-3 text-sky-300">Patch Notes</h3>
                                <ul className="space-y-2">
                                    {renderContent(announcement.patchNotes)}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AnnouncementDisplay;